import type { SyntheticEvent } from "react"
import { useCallback, useState } from "react"
import { navigate } from "gatsby"
import type { DomainCheck } from "@onestore/api/domainSearch"
import type { LocationDomainSearchState } from "@gatsby-plugin-domain-search/hooks/useDomainHashChangeEffect"
import useTypingPlaceholder from "@gatsby-plugin-domain-search/hooks/useTypingPlaceholder"
import { buildDomainHash } from "@gatsby-plugin-domain-search/lib/domains"
import type { SearchDomainExtension } from "@gatsby-plugin-generic-page/fragments/searchSection"
import DomainExtensionButtons from "@gatsby-plugin-generic-page/sections/SearchSection/components/DomainExtensionButtons"
import { getDomainParts, validatePhrase } from "~/lib/domain"
import isEmpty from "~/lib/isEmpty"
import url from "~/lib/url"
import SingleLineDomainSearchBar from "../SingleLineDomainSearchBar"

export type DomainInputFieldProps = {
  placeholderText: string
  extension?: DomainCheck.Extension
  afterSubmit?: { (): void }
  hasNoTextOnButton?: boolean
  domainExtensions?: SearchDomainExtension[]
  defaultExtensionIndex?: number
  hasAnimatedPlaceholder?: boolean
  searchResultsUrl?: string
}

function getDefaultExtension(
  domainExtensions: SearchDomainExtension[] | undefined,
  defaultExtensionIndex: number | undefined
): string | null {
  if (isEmpty(domainExtensions) || !defaultExtensionIndex) {
    return null
  }

  const defaultItem = domainExtensions[defaultExtensionIndex - 1]

  return defaultItem?.domain[0]?.flatData.extension ?? null
}

export default function DomainInputField({
  placeholderText,
  extension,
  afterSubmit,
  hasNoTextOnButton,
  domainExtensions,
  defaultExtensionIndex,
  hasAnimatedPlaceholder,
  searchResultsUrl,
}: DomainInputFieldProps) {
  const defaultSelectedExtension = getDefaultExtension(
    domainExtensions,
    defaultExtensionIndex
  )

  const [valid, setValid] = useState(true)
  const [value, setValue] = useState(
    defaultSelectedExtension ? `.${defaultSelectedExtension}` : ""
  )
  const [componentText, setError] = useState(placeholderText)
  const [selectedDomainExtension, setSelectedDomainExtension] = useState<
    string | null
  >(defaultSelectedExtension)

  const applyValue = (newValue: string): void => {
    setValue(newValue)
    const validation = validatePhrase(newValue)
    setValid(validation.valid)
    setError(validation.valid ? placeholderText : validation.errors[0])
  }

  const animatedPlaceholder = useTypingPlaceholder({
    text: hasAnimatedPlaceholder ? placeholderText : "",
  })

  const onChange = (
    eventOrValue: SyntheticEvent<HTMLInputElement> | string
  ): void => {
    const newValue =
      typeof eventOrValue === "string"
        ? eventOrValue
        : eventOrValue.currentTarget.value

    // The active extension is a locked suffix once selected — typing or deleting
    // in the input must never be able to touch it, only the extension buttons can.
    if (
      selectedDomainExtension &&
      !newValue.endsWith(`.${selectedDomainExtension}`)
    ) {
      return
    }

    applyValue(newValue)
  }

  const onSelectDomainExtension = useCallback(
    (selectedExtension: string): void => {
      const [name] = getDomainParts(value)
      const isDeselecting = selectedDomainExtension === selectedExtension

      setSelectedDomainExtension(isDeselecting ? null : selectedExtension)
      applyValue(isDeselecting ? name : `${name}.${selectedExtension}`)
    },
    [value, selectedDomainExtension]
  )

  const onInputInteraction = (event: SyntheticEvent<HTMLDivElement>): void => {
    const inputElement = event.target

    if (
      !(inputElement instanceof HTMLInputElement) ||
      !selectedDomainExtension
    ) {
      return
    }

    const namePosition =
      inputElement.value.length - (selectedDomainExtension.length + 1)

    inputElement.setSelectionRange(namePosition, namePosition)
  }

  const onSubmit = useCallback((): void => {
    if (value.length === 0 || !valid) {
      return
    }

    if (searchResultsUrl) {
      navigate(`${searchResultsUrl}?q=${encodeURIComponent(value)}`)
    } else {
      const hash = buildDomainHash({
        d: value,
        e: extension,
      })

      navigate(url.getDomainSearchUrl() || "", {
        state: {
          hash,
          rawSearchValue: value,
        } as LocationDomainSearchState,
        replace: false,
      })
    }

    if (afterSubmit) {
      afterSubmit()
    }
  }, [value, extension, valid, searchResultsUrl])

  return (
    <div
      className="contents"
      onClick={onInputInteraction}
      onFocus={onInputInteraction}
    >
      <SingleLineDomainSearchBar
        value={value}
        onChange={onChange}
        onSearch={onSubmit}
        errorMessage={!valid ? componentText : undefined}
        hasNoTextOnButton={hasNoTextOnButton}
        placeholder={hasAnimatedPlaceholder ? animatedPlaceholder : undefined}
        suffix={
          !isEmpty(domainExtensions) ? (
            <DomainExtensionButtons
              extensions={domainExtensions}
              selected={selectedDomainExtension}
              onSelect={onSelectDomainExtension}
            />
          ) : undefined
        }
      />
    </div>
  )
}
